import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Wallet, TrendingUp, TrendingDown, AlertTriangle, Calendar, PiggyBank,
  Loader2, Plus, ArrowRight, Sparkles, FileDown, LineChart as LineChartIcon,
} from "lucide-react";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTip,
  ResponsiveContainer, Legend,
} from "recharts";
import { api } from "../lib/api";
import Tip from "../components/Tip";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n || 0));
const eurAxis = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(0)}k€`;
  return `${v.toFixed(0)}€`;
};

export default function Finances() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [cash, setCash] = useState(null);
  const [cashLoading, setCashLoading] = useState(false);
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      await api.financesSeedTemplates().catch(() => {});
      setD(await api.financesDashboard());
    } catch (e) {
      setErr(e?.response?.data?.detail || "Errore caricamento");
    } finally { setLoading(false); }
  };
  const loadCashflow = async () => {
    setCashLoading(true);
    try { setCash(await api.financesCashflow(12)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore grafico cashflow"); }
    finally { setCashLoading(false); }
  };
  const runAi = async () => {
    setAiLoading(true); setAiOpen(true);
    try {
      const r = await api.financesAiSuggest();
      setAi(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore analisi AI");
      setAiOpen(false);
    } finally { setAiLoading(false); }
  };

  useEffect(() => { load(); loadCashflow(); }, []);

  if (loading) return <div className="flex items-center gap-2 opacity-70" data-testid="finances-loading"><Loader2 size={16} className="animate-spin" /> Caricamento…</div>;
  if (err) return <div className="pxos-card p-6 border-2" style={{ borderColor: "#F5C4C4", background: "#FFF5F5", color: "#B42318" }} data-testid="finances-error">Errore: {err}</div>;

  return (
    <div className="space-y-6" data-testid="finances-dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest opacity-70">Finanze</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold">Panoramica finanziaria</h1>
          <p className="mt-2 text-sm opacity-80 max-w-2xl">
            Pianificazione personale + business. I dati mostrati vengono <b>solo</b> da voci inserite da te; nessun importo viene inventato.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Tip label="Scarica il PDF mensile">
            <Link to="/finances/reports" className="rounded-full border px-3 py-1.5 text-sm inline-flex items-center gap-1 hover:bg-black/5"
                  data-testid="finances-goto-reports">
              <FileDown size={14} /> Report PDF
            </Link>
          </Tip>
          <Tip label="Vai alla lista voci finanziarie">
            <Link to="/finances/items" className="rounded-full border px-3 py-1.5 text-sm inline-flex items-center gap-1 hover:bg-black/5"
                  data-testid="finances-goto-items">
              Tutte le voci <ArrowRight size={14} />
            </Link>
          </Tip>
          <Tip label="Nuova voce finanziaria">
            <Link to="/finances/items?new=1" className="pxos-btn-primary inline-flex items-center gap-1.5 text-sm" data-testid="finances-new-item">
              <Plus size={14} /> Nuova voce
            </Link>
          </Tip>
        </div>
      </div>

      {/* Quanto posso spendere — CARD GRANDE */}
      <Tip label={`Calcolo: saldo (${eur(d.saldo_totale_conti)}) − vincolati (${eur(d.uscite_mese_pending)}) − fondo minimo (${eur(d.minimum_reserve)})`}>
        <div className="pxos-card p-6 border-2 flex items-center gap-4" style={{ borderColor: "#B6E0C4", background: "linear-gradient(135deg, #F0FBF4 0%, #FFFFFF 100%)" }} data-testid="card-quanto-spendere">
          <div className="h-14 w-14 rounded-full grid place-items-center" style={{ background: "#B6E0C4" }}>
            <Wallet size={26} style={{ color: "#1F7A2C" }} />
          </div>
          <div className="flex-1">
            <div className="text-xs font-mono uppercase tracking-widest opacity-70">Quanto posso spendere questo mese</div>
            <div className="mt-1 font-display text-4xl font-semibold" style={{ color: "#1F7A2C" }} data-testid="quanto-spendere-value">
              {eur(d.quanto_posso_spendere)}
            </div>
          </div>
        </div>
      </Tip>

      {/* Grid mini cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniCard icon={Wallet} label="Saldo totale conti" value={eur(d.saldo_totale_conti)} testid="card-saldo" />
        <MiniCard icon={TrendingUp} label="Entrate mese" value={eur(d.entrate_mese)} color="#1F7A2C" testid="card-entrate" />
        <MiniCard icon={TrendingDown} label="Uscite pending mese" value={eur(d.uscite_mese_pending)} color="#B42318" testid="card-uscite-pending" />
        <MiniCard icon={PiggyBank} label="Da accantonare/mese" value={eur(d.da_accantonare_mensile)} testid="card-accantonare" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniCard icon={Calendar} label="Scadenze 7 giorni" value={d.scadenze_7d} testid="card-scad-7d" />
        <MiniCard icon={Calendar} label="Scadenze 30 giorni" value={d.scadenze_30d} testid="card-scad-30d" />
        <MiniCard icon={Calendar} label="Scadenze 60 giorni" value={d.scadenze_60d} testid="card-scad-60d" />
        <MiniCard icon={Calendar} label="Scadenze 90 giorni" value={d.scadenze_90d} testid="card-scad-90d" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <MiniCard icon={TrendingDown} label="Costi fissi mensili" value={eur(d.costi_fissi_mensili)} testid="card-fissi" />
        <MiniCard icon={TrendingDown} label="Costi annuali /12" value={eur(d.costi_annuali_equivalente_mensile)} testid="card-annuali-eq" />
        <MiniCard icon={TrendingDown} label="Forecast uscite 12 mesi" value={eur(d.forecast_12m)} testid="card-forecast-12m" />
      </div>

      {/* Cashflow chart */}
      <div className="pxos-card p-4 sm:p-6" data-testid="card-cashflow">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-full grid place-items-center bg-black/5">
              <LineChartIcon size={16} style={{ color: "var(--pxos-text-dark)" }} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-60">Cashflow previsionale</div>
              <div className="font-display text-lg font-semibold">Prossimi 12 mesi</div>
            </div>
          </div>
          {cashLoading && <Loader2 size={14} className="animate-spin opacity-60" />}
        </div>
        <div style={{ width: "100%", height: 300 }}>
          {cash?.points?.length ? (
            <ResponsiveContainer>
              <LineChart data={cash.points} margin={{ top: 6, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid stroke="#EEF2F7" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#6B7280" }} angle={-30} textAnchor="end" height={54} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: "#6B7280" }} tickFormatter={eurAxis} width={60} />
                <RTip
                  formatter={(v) => eur(v)}
                  labelStyle={{ color: "#4A6078", fontWeight: 600, fontSize: 12 }}
                  contentStyle={{ background: "white", border: "1px solid #C3DAF4", borderRadius: 10, fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="entrate" name="Entrate" stroke="#1F7A2C" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="uscite" name="Uscite" stroke="#B42318" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="saldo_previsto" name="Saldo previsto" stroke="#4A6078" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full grid place-items-center opacity-60 text-sm" data-testid="cashflow-empty">
              {cashLoading ? "Caricamento grafico…" : "Nessun dato disponibile per il grafico."}
            </div>
          )}
        </div>
        <div className="text-[11px] opacity-60 mt-2">
          Basato solo sulle scadenze inserite. Nessuna previsione automatica: se una voce non ha data o importo, non compare qui.
        </div>
      </div>

      {/* AI Suggest card */}
      <div className="pxos-card p-4 sm:p-6 border-2" style={{ borderColor: "#DDD3F0", background: "linear-gradient(135deg, #F7F3FF 0%, #FFFFFF 60%)" }} data-testid="card-ai-suggest">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-full grid place-items-center" style={{ background: "#DDD3F0" }}>
              <Sparkles size={16} style={{ color: "#5B21B6" }} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-60">Segretaria IA · Analisi finanza</div>
              <div className="font-display text-lg font-semibold">Suggerimenti sulla tua situazione</div>
              <div className="text-xs opacity-70">Analisi organizzativa (non consulenza finanziaria). Solo dati che hai inserito tu.</div>
            </div>
          </div>
          <Tip label="Genera analisi con la Segretaria IA (rate limit 20/ora)">
            <button onClick={runAi} disabled={aiLoading} className="pxos-btn-primary inline-flex items-center gap-1.5 text-sm disabled:opacity-60" data-testid="ai-suggest-btn">
              {aiLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {aiLoading ? "Analisi in corso…" : (ai ? "Rigenera analisi" : "Genera analisi")}
            </button>
          </Tip>
        </div>

        {aiOpen && (
          <div className="mt-4 space-y-3" data-testid="ai-suggest-panel">
            {aiLoading && !ai && (
              <div className="text-sm opacity-70 flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Sto analizzando…
              </div>
            )}
            {ai?.warning && (
              <div className="pxos-card p-3 border-2 text-sm" style={{ borderColor: "#F5C24A", background: "#FFF8E1", color: "#8A5A00" }} data-testid="ai-warning">
                {ai.warning}
              </div>
            )}
            {ai?.analisi && (
              <div className="text-sm whitespace-pre-wrap leading-relaxed" data-testid="ai-analisi">
                {ai.analisi}
              </div>
            )}
            {ai?.dati_mancanti?.length > 0 && (
              <div className="pxos-card p-3 text-sm" style={{ background: "#FFF8E1" }} data-testid="ai-missing">
                <div className="font-semibold mb-2" style={{ color: "#8A5A00" }}>Dati mancanti da completare</div>
                <ul className="space-y-1.5" style={{ color: "#8A5A00" }}>
                  {ai.dati_mancanti.map((m, i) => {
                    const label = typeof m === "string" ? m : (m?.label || m?.text || JSON.stringify(m));
                    // Extract a probable item name from the label ("Importo ENPAM (contributo…)")
                    const nameMatch = String(label).match(/(?:Importo|Data|Scadenza)?\s+([A-Z][^\(]+?)(?:\s*\(|$)/);
                    const nameGuess = (nameMatch ? nameMatch[1] : label).trim().slice(0, 80);
                    const href = `/finances/items?new=1&name=${encodeURIComponent(nameGuess)}&status=template`;
                    return (
                      <li key={i} className="flex items-start gap-2">
                        <span className="mt-0.5">•</span>
                        <Link to={href}
                              className="underline decoration-dotted hover:decoration-solid font-medium"
                              style={{ color: "#8A5A00" }}
                              data-testid={`ai-missing-cta-${i}`}
                              title="Apri il modulo pre-compilato per completare questa voce">
                          {label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
                <div className="text-[10px] opacity-70 mt-2">Clicca su una voce per aprire il modulo pre-compilato.</div>
              </div>
            )}
            {ai?.suggerimenti?.length > 0 && (
              <div className="space-y-2" data-testid="ai-suggerimenti">
                {ai.suggerimenti.map((s, i) => (
                  <div key={i} className="pxos-card p-3 text-sm flex items-start gap-2">
                    <Sparkles size={14} className="mt-0.5 shrink-0" style={{ color: "#5B21B6" }} />
                    <div className="flex-1">
                      {s.tipo && <div className="text-[10px] uppercase tracking-widest opacity-70 mb-0.5">{s.tipo}</div>}
                      <div>{s.testo}</div>
                      {s.importo != null && <div className="text-xs opacity-70 mt-1">Importo indicativo: {eur(s.importo)}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {ai?.dati_utilizzati?.length > 0 && (
              <details className="text-xs opacity-70" data-testid="ai-dati-utilizzati">
                <summary className="cursor-pointer">Dati usati per questa analisi ({ai.dati_utilizzati.length})</summary>
                <ul className="list-disc pl-5 mt-1 space-y-0.5">
                  {ai.dati_utilizzati.map((m, i) => <li key={i}>{m}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      {/* Dati mancanti */}
      {d.dati_mancanti > 0 && (
        <div className="pxos-card p-4 border-2 flex items-start gap-3" style={{ borderColor: "#F5C24A", background: "#FFF8E1" }} data-testid="card-dati-mancanti">
          <AlertTriangle size={20} style={{ color: "#8A5A00" }} className="mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold" style={{ color: "#8A5A00" }}>Dati da completare</div>
            <div className="text-sm mt-1" style={{ color: "#8A5A00" }}>
              Ci sono <b>{d.dati_mancanti} elementi</b> senza importo o ancora in stato template. Completali per ricevere calcoli più accurati.
            </div>
          </div>
          <Link to="/finances/items?status=template" className="rounded-full border px-3 py-1.5 text-xs font-semibold whitespace-nowrap"
                style={{ borderColor: "#8A5A00", color: "#8A5A00" }} data-testid="btn-completa-dati">
            Completa dati
          </Link>
        </div>
      )}
    </div>
  );
}

function MiniCard({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="pxos-card p-3 flex items-center gap-3" data-testid={testid}>
      <div className="h-9 w-9 rounded-full grid place-items-center bg-black/5">
        <Icon size={16} style={{ color: color || "var(--pxos-text-dark)" }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest opacity-60 truncate" style={{ color: "var(--pxos-text-dark)" }}>{label}</div>
        <div className="font-display text-lg font-semibold truncate" style={{ color: color || "var(--pxos-text-dark)" }}>{value}</div>
      </div>
    </div>
  );
}
